/**
 * Pricing Modal
 * Shows subscription plans and handles checkout
 *
 * This modal dynamically renders tiers and pricing from the tier configuration
 * service, which fetches data from Supabase. This allows for custom tier names,
 * pricing, and features without code changes.
 */

import { checkoutService } from './checkout-service.js';
import { paypalService } from './paypal-service.js';
import { authService } from '../auth/auth-service.js';
import { authUI } from '../auth/auth-ui.js';
import { entitlementService } from '../entitlements/entitlement-service.js';
import { tierConfigService } from '../config/tier-config-service.js';
import { escapeHtml } from '../lib/html-utils.js';
import { trackPricingModalOpened } from '../app/analytics/event-tracker.js';

class PricingModal {
  constructor() {
    this.modal = null;
    this.isOpen = false;
    this.paypalInitialized = false;
    this.tierConfig = null;
  }

  /**
   * Initialize the pricing modal
   */
  init() {
    this.createModal();
    this.attachEventListeners();
  }

  /**
   * Create the pricing modal HTML (placeholder structure)
   */
  createModal() {
    const modalHTML = `
      <div id="pricingModal" class="pricing-modal" style="display: none;">
        <div class="pricing-modal-backdrop"></div>
        <div class="pricing-modal-content">
          <button class="pricing-modal-close">&times;</button>

          <h2 class="pricing-title">Choose Your Plan</h2>
          <p class="pricing-subtitle">Unlock all visualizers, higher resolutions, and more exports</p>

          <div class="pricing-toggle">
            <span class="pricing-toggle-label" data-period="monthly">Monthly</span>
            <label class="pricing-toggle-switch">
              <input type="checkbox" id="pricingPeriodToggle">
              <span class="pricing-toggle-slider"></span>
            </label>
            <span class="pricing-toggle-label" data-period="annual">Annual <span class="pricing-save">Save 17%</span></span>
          </div>

          <div class="pricing-plans" id="pricingPlansContainer">
            <!-- Plans will be dynamically rendered -->
            <div class="pricing-loading">Loading plans...</div>
          </div>

          <!-- Credits Section -->
          <div class="pricing-credits" id="pricingCreditsContainer">
            <h3>Need More Exports?</h3>
            <p>Buy credit packs for additional export minutes beyond your plan</p>
            <div class="pricing-credits-options" id="creditPacksContainer">
              <!-- Credit packs will be dynamically rendered -->
            </div>
            <p class="pricing-credits-info" id="creditsPerMinuteInfo"></p>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);
    this.modal = document.getElementById('pricingModal');
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // Close modal
    this.modal.querySelector('.pricing-modal-backdrop').addEventListener('click', () => this.hide());
    this.modal.querySelector('.pricing-modal-close').addEventListener('click', () => this.hide());

    // Period toggle
    const toggle = this.modal.querySelector('#pricingPeriodToggle');
    toggle.addEventListener('change', () => this.updatePrices(toggle.checked));

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.hide();
      }
    });
  }

  /**
   * Render tier plans from config
   */
  renderPlans(tiers, currentTier) {
    const container = this.modal.querySelector('#pricingPlansContainer');
    if (!container) return;

    // Filter to tiers that should be shown (purchasable or free tier for comparison)
    const visibleTiers = tiers.filter(t => t.isPurchasable || !t.isPaid);

    const plansHTML = visibleTiers.map(tier => {
      const isCurrent = tier.id === currentTier;
      const isFeatured = tier.highlightLabel === 'Most Popular';
      const monthlyPricing = tier.pricing.find(p => p.billingPeriod === 'monthly');
      const annualPricing = tier.pricing.find(p => p.billingPeriod === 'annual');

      // Calculate monthly equivalent for annual pricing
      const annualMonthly = annualPricing ? (annualPricing.price / 12).toFixed(2) : null;

      const featuresHTML = tier.features
        .map(f => {
          const highlighted = f.isHighlighted ? '<strong>' : '';
          const highlightedEnd = f.isHighlighted ? '</strong>' : '';
          return `<li>${highlighted}${escapeHtml(f.displayLabel)}${highlightedEnd}</li>`;
        })
        .join('');

      const priceDisplay = tier.isPaid && monthlyPricing
        ? `<span class="pricing-amount" data-monthly="$${monthlyPricing.price}" data-annual="${annualMonthly ? `$${annualMonthly}` : ''}">$${monthlyPricing.price}</span>`
        : '<span class="pricing-amount">$0</span>';

      const buttonText = isCurrent ? 'Current Plan' : (tier.isPaid ? 'Subscribe' : 'Free Plan');
      const buttonAction = isCurrent || !tier.isPaid ? 'current' : `subscribe-${tier.id}`;
      const buttonClass = isFeatured && !isCurrent ? 'btn btn-primary pricing-plan-btn' : 'btn pricing-plan-btn';
      const buttonDisabled = isCurrent || !tier.isPaid ? 'disabled' : '';

      return `
        <div class="pricing-plan ${isFeatured ? 'pricing-plan-featured' : ''}" data-plan="${tier.id}">
          ${tier.highlightLabel ? `<div class="pricing-plan-badge">${escapeHtml(tier.highlightLabel)}</div>` : ''}
          <div class="pricing-plan-header">
            <h3>${escapeHtml(tier.displayName)}</h3>
            <div class="pricing-plan-price">
              ${priceDisplay}
              <span class="pricing-period">/month</span>
            </div>
          </div>
          <ul class="pricing-features">
            ${featuresHTML}
          </ul>
          <button class="${buttonClass}" data-action="${buttonAction}" data-tier="${tier.id}" ${buttonDisabled}>${buttonText}</button>
        </div>
      `;
    }).join('');

    container.innerHTML = plansHTML;

    // Re-attach button listeners
    container.querySelectorAll('.pricing-plan-btn').forEach(btn => {
      btn.addEventListener('click', () => this.handlePlanAction(btn.dataset.action, btn.dataset.tier));
    });
  }

  /**
   * Render credit packs from config
   */
  renderCreditPacks(creditPacks, creditsPerMinute) {
    const container = this.modal.querySelector('#creditPacksContainer');
    const infoEl = this.modal.querySelector('#creditsPerMinuteInfo');

    if (!container) return;

    const packsHTML = creditPacks.map(pack => {
      return `
        <div class="credit-pack" data-pack="${pack.id}">
          <div class="credit-pack-info">
            <span class="credit-pack-name">${escapeHtml(pack.name)}</span>
            <span class="credit-pack-price">$${pack.price.toFixed(2)}</span>
            <span class="credit-pack-desc">${escapeHtml(pack.description) || `${pack.credits} credits`}</span>
          </div>
          <div class="credit-pack-buttons">
            <button class="btn btn-small" data-action="buy-credits" data-pack-id="${pack.id}" data-stripe-price="${pack.stripePriceId || ''}">Card</button>
            <div id="paypal-${pack.id}" class="paypal-btn-container"></div>
          </div>
        </div>
      `;
    }).join('');

    container.innerHTML = packsHTML;

    // Update credits per minute info
    if (infoEl) {
      infoEl.textContent = `${creditsPerMinute} credits = 1 minute of export`;
    }

    // Re-attach button listeners
    container.querySelectorAll('[data-action="buy-credits"]').forEach(btn => {
      btn.addEventListener('click', () => this.handleCreditPurchase(btn.dataset.packId, btn.dataset.stripePrice));
    });
  }

  /**
   * Update prices based on billing period
   */
  updatePrices(isAnnual) {
    // Update all price displays
    this.modal.querySelectorAll('.pricing-amount[data-monthly]').forEach(priceEl => {
      const monthly = priceEl.dataset.monthly;
      const annual = priceEl.dataset.annual;
      priceEl.textContent = isAnnual && annual ? annual : monthly;
    });

    // Update toggle labels
    this.modal.querySelectorAll('.pricing-toggle-label').forEach(label => {
      label.classList.toggle('active', label.dataset.period === (isAnnual ? 'annual' : 'monthly'));
    });
  }

  /**
   * Handle plan subscription action
   */
  async handlePlanAction(action, tierId) {
    if (action === 'current') return;

    // Check if user is authenticated
    if (!authService.isAuthenticated()) {
      this.hide();
      authUI.showModal('signup');
      authUI.onAuthSuccess = () => this.show();
      return;
    }

    try {
      // Show loading state
      const btn = this.modal.querySelector(`[data-tier="${tierId}"]`);
      const originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Redirecting...';

      const isAnnual = this.modal.querySelector('#pricingPeriodToggle').checked;
      const tier = this.tierConfig?.tiers.find(t => t.id === tierId);

      if (!tier) {
        throw new Error('Tier not found');
      }

      // Get the appropriate pricing
      const pricing = tier.pricing.find(p => p.billingPeriod === (isAnnual ? 'annual' : 'monthly'));

      if (!pricing?.stripePriceId) {
        throw new Error('Pricing not configured for this tier');
      }

      await checkoutService.checkout(pricing.stripePriceId, {
        tier: tierId,
        billingPeriod: isAnnual ? 'annual' : 'monthly',
        price: pricing.price
      });
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Checkout failed: ' + error.message);

      // Reset button
      const btn = this.modal.querySelector(`[data-tier="${tierId}"]`);
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Subscribe';
      }
    }
  }

  /**
   * Handle credit pack purchase
   */
  async handleCreditPurchase(packId, stripePriceId) {
    // Check if user is authenticated
    if (!authService.isAuthenticated()) {
      this.hide();
      authUI.showModal('signup');
      authUI.onAuthSuccess = () => this.show();
      return;
    }

    try {
      const btn = this.modal.querySelector(`[data-pack-id="${packId}"]`);
      const originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Redirecting...';

      if (!stripePriceId) {
        throw new Error('Credit pack not configured');
      }

      // Get pack info for tracking
      const pack = this.tierConfig?.creditPacks?.find(p => p.id === packId);

      await checkoutService.checkout(stripePriceId, {
        tier: `credits_${packId}`,
        billingPeriod: 'one_time',
        price: pack?.price || 0
      });
    } catch (error) {
      console.error('Credit purchase error:', error);
      alert('Purchase failed: ' + error.message);

      const btn = this.modal.querySelector(`[data-pack-id="${packId}"]`);
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Card';
      }
    }
  }

  /**
   * Show the pricing modal
   * @param {string} triggerLocation - Where the modal was triggered from
   */
  async show(triggerLocation = 'unknown') {
    // Track pricing modal opened
    trackPricingModalOpened(triggerLocation);

    this.modal.style.display = 'flex';
    this.isOpen = true;

    try {
      // Fetch tier configuration
      this.tierConfig = await tierConfigService.getTierConfig();
      const currentTier = await entitlementService.getTier();

      // Render plans and credit packs
      this.renderPlans(this.tierConfig.tiers, currentTier);
      this.renderCreditPacks(this.tierConfig.creditPacks, this.tierConfig.settings.creditsPerMinute);

      // Initialize PayPal buttons if available and user is authenticated
      if (authService.isAuthenticated() && !this.paypalInitialized) {
        await this.initPayPalButtons();
      }
    } catch (error) {
      console.error('Error loading pricing:', error);
      const container = this.modal.querySelector('#pricingPlansContainer');
      if (container) {
        container.innerHTML = '<div class="pricing-error">Unable to load pricing. Please try again.</div>';
      }
    }
  }

  /**
   * Initialize PayPal buttons for credit packs
   */
  async initPayPalButtons() {
    const isAvailable = await paypalService.init();
    if (!isAvailable) {
      // Hide PayPal containers if not available
      document.querySelectorAll('.paypal-btn-container').forEach(el => {
        el.style.display = 'none';
      });
      return;
    }

    this.paypalInitialized = true;

    const handleSuccess = (result) => {
      this.hide();
      alert(`Success! ${result.credits} credits added to your account.`);
      entitlementService.clearCache();
    };

    const handleError = (error) => {
      alert('Payment failed: ' + error.message);
    };

    // Create PayPal buttons for each credit pack
    const creditPacks = this.tierConfig?.creditPacks || [];
    for (const pack of creditPacks) {
      const containerId = `paypal-${pack.id}`;
      const container = document.getElementById(containerId);
      if (container && pack.paypalProductId) {
        paypalService.createButtons(containerId, pack.paypalProductId, {
          onSuccess: handleSuccess,
          onError: handleError,
          onUseStripe: () => {
            // PayPal unavailable for this product — fall back to Stripe
            console.log(`[PricingModal] PayPal unavailable for ${pack.id}, falling back to Stripe`);
            if (pack.stripePriceId) {
              this.handleCreditPurchase(pack.id, pack.stripePriceId);
            }
          },
        });
      }
    }
  }

  /**
   * Hide the pricing modal
   */
  hide() {
    this.modal.style.display = 'none';
    this.isOpen = false;
  }
}

// Export singleton instance
export const pricingModal = new PricingModal();
